// app/DeviceBootstrap.jsx — extracted from App.js (behaviour-preserving split).
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Settings } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { getSouthAfricanSeason } from "@/lib/theme";
import { onInstallLinkIntent } from "@/lib/platform";
import { applyRemoteEvent, LOCAL_EVENT_APPLIED } from "@/lib/eventEngine";
import { armEventSync, configureEventSync } from "@/lib/eventSync";
import { armSnapshotRefresh, configureSnapshotRefresh } from "@/lib/snapshotRefresh";
import { destroyMesh, fireAndForgetMesh, getMeshStatus, initMesh, subscribeMesh } from "@/lib/lanMesh";
import { setCoordinator } from "@/lib/coordinator";
import NativeBootPage from "@/pages/NativeBoot";
import { getDeviceId, getDeviceLabel, isInstalledAppEnvironment, isStandaloneLaunch, resolveLandingPathForDevice } from "@/lib/device";
import {
  _clearPendingRoleSessions,
  _isSessionHeartbeatFresh,
  _sessionAgeExpired,
  _writeHeartbeat,
  BACKEND_URL,
  clearSession,
  getSession,
} from "@/app/core";
import { forEachStoredSession } from "@/lib/authStorage";

const HEARTBEAT_INTERVAL_MS = 20 * 1000;
let _heartbeatTimer = null;

// Called ONCE per page load, very early. If the heartbeat is stale we
// wipe every PENDING role session before any component reads it.
// Confirmed sessions (stored in localStorage) survive. Then start
// the heartbeat so future reloads / navigations know we're still alive.
export function initSessionHeartbeat() {
  if (typeof window === "undefined") return;
  if (!_isSessionHeartbeatFresh()) {
    _clearPendingRoleSessions();
  }
  _writeHeartbeat();
  if (_heartbeatTimer) window.clearInterval(_heartbeatTimer);
  _heartbeatTimer = window.setInterval(_writeHeartbeat, HEARTBEAT_INTERVAL_MS);
  // Also write on visibility change so a tab that spent hours in the
  // background but has just been re-focused doesn't get wiped mid-use.
  const onVis = () => { if (document.visibilityState === "visible") _writeHeartbeat(); };
  document.addEventListener("visibilitychange", onVis);
}

// Resolve where `/` should land:
//   • Installed PWA whose device-role is set → go straight to that role's
//     login (which auto-fills the persisted business code).
//   • Anyone else (browser tab, no device config) → default to the
//     customer menu (existing behaviour).
export function DeviceRoot() {
  // Session 30.11 — Installed apps (APK / EXE) boot through the new
  // Splash → Business Code → Dashboard picker flow. The old "choose
  // dashboard" install wizard is no longer used for native launches.
  let native = false;
  try { native = isInstalledAppEnvironment(); } catch (_) { native = false; }
  if (native) return <NativeBootPage getSession={getSession} />;
  const landing = (() => {
    try {
      if (!isStandaloneLaunch()) return null;
      return resolveLandingPathForDevice();
    } catch (_) { return null; }
  })();
  if (landing) return <Navigate to={landing} replace />;
  // Standalone launch with NO device role configured yet → send them
  // through the install/role-picker flow. This handles the case where
  // the user cleared app data or is launching for the first time.
  try {
    if (isStandaloneLaunch()) return <Navigate to="/install" replace />;
  } catch (_) { /* noop */ }
  return <Navigate to="/menu" replace />;
}

// One-shot bootstrap: makes the device-id concrete in localStorage so
// Phase-2 remote-device management has a stable identifier to refer to,
// AND runs the session-heartbeat check so any "stale" sessions
// restored by the browser tab-restore feature are wiped before any
// component tries to read them.
export function DeviceBootstrap() {
  const navigate = useNavigate();
  useEffect(() => {
    try { getDeviceId(); } catch (_) { /* noop */ }
    try { initSessionHeartbeat(); } catch (_) { /* noop */ }
  }, []);

  // Session 26 — arm the Local Event Engine sync loop.
  //
  // The engine writes every mutation to IDB first (see
  // eventEngine.localWrite) and this loop drains the resulting
  // queue toward /api/events/sync in the background.
  //
  // We supply a token-lookup callback rather than a static token so
  // the loop transparently follows role switches (staff → owner
  // handover, kitchen ↔ bar, etc.) and cross-tab sessions. The
  // tokens live in localStorage keyed by role — pick any that has
  // a valid session, prioritising staff since that's where writes
  // originate today.
  useEffect(() => {
    const tokenSupplier = () => {
      for (const role of ["staff", "owner", "kitchen", "bar", "admin"]) {
        const s = getSession(role);
        if (s && s.token) return s.token;
      }
      return null;
    };
    configureEventSync({ tokenSupplier });
    armEventSync();

    // Session 27 — arm the inbound snapshot refresh loop so
    // deactivations / PIN resets / config changes flow into the
    // device automatically without any user action. Non-blocking
    // (first refresh delayed ~30s from arming).
    configureSnapshotRefresh({ tokenSupplier });
    armSnapshotRefresh();
  }, []);

  // Session 30.2 — 24-hour session watchdog for ACTIVE tabs. getSession
  // already enforces the TTL lazily on read, but a dashboard left open
  // untouched wouldn't hit that path — this interval sweeps every
  // minute, wipes expired sessions, and kicks the on-screen dashboard
  // back to its login page.
  useEffect(() => {
    const ROLE_LOGIN_PATH = {
      staff: "/staff", owner: "/owner", kitchen: "/kitchen",
      bar: "/bar", admin: "/admin", retail: "/retail",
    };
    const sweep = () => {
      try {
        let kickedPath = null;
        const seg = (window.location.pathname.split("/")[1] || "").toLowerCase();
        forEachStoredSession((role, parsed) => {
          try {
            const loginAt = parsed && parsed._login_at ? new Date(parsed._login_at).getTime() : NaN;
            // Session 46 — idle-aware cap (see _sessionAgeExpired).
            if (_sessionAgeExpired(loginAt)) {
              clearSession(role, "session_max_age_24h_sweep");
              // Runner rides on the staff session; map both paths.
              const affectedSegs = role === "staff" ? ["staff", "runner"] : [role];
              if (affectedSegs.includes(seg)) kickedPath = ROLE_LOGIN_PATH[role] || "/";
            }
          } catch (_) { /* noop */ }
          return false;
        });
        if (kickedPath) {
          try { toast.info("Your 24-hour session has ended — please sign in again."); } catch (_) { /* noop */ }
          window.setTimeout(() => window.location.assign(kickedPath), 400);
        }
      } catch (_) { /* noop */ }
    };
    const id = window.setInterval(sweep, 60 * 1000);
    return () => window.clearInterval(id);
  }, []);

  // Session 27.8 — LAN Mesh lifecycle.
  //
  // Whenever any role has a valid session we initialise the WebRTC
  // signalling socket + peer manager. The mesh is a background
  // transport — it stays connected as long as we're logged in and
  // silently pairs with any other tablet in the same business room.
  //
  // Incoming events from peers are applied to the local IndexedDB
  // (via applyRemoteEvent) AND surfaced as a DOM event so any
  // dashboard subscriber can react (e.g. Staff plays a chime when
  // Kitchen marks an order ready).
  //
  // The token supplier polls localStorage every second so cross-tab
  // sign-in/sign-out is picked up without a full page reload.
  useEffect(() => {
    let currentToken = null;
    let currentRole = null;
    let unsubMesh = () => {};

    const pickSession = () => {
      // Session 30.1 — Prefer the session that matches the dashboard
      // currently on screen so the mesh role always reflects reality
      // (a tablet that switched Kitchen → Bar without logging out
      // used to keep announcing itself as "kitchen"). Falls back to
      // first-match ordering when the path isn't a dashboard. Also
      // added the previously-missing "retail" session so Retail POS
      // devices join the mesh, and mapped /runner/dashboard onto the
      // staff session it actually uses.
      const ROLES = ["staff", "owner", "kitchen", "bar", "admin", "retail"];
      let preferred = null;
      try {
        const seg = (window.location.pathname.split("/")[1] || "").toLowerCase();
        preferred = seg === "runner" ? "staff" : (ROLES.includes(seg) ? seg : null);
      } catch (_) { preferred = null; }
      const ordered = preferred ? [preferred, ...ROLES.filter((r) => r !== preferred)] : ROLES;
      for (const role of ordered) {
        const s = getSession(role);
        if (s && s.token && s.business) return { token: s.token, role, business: s.business };
      }
      return null;
    };

    const sync = () => {
      const picked = pickSession();
      if (!picked) {
        if (currentToken) {
          try { destroyMesh(); } catch (_) { /* noop */ }
          currentToken = null;
          currentRole = null;
        }
        return;
      }
      const businessId = picked.business?.id || picked.business?.slug || null;
      if (!businessId) return;
      // Session 27.73 — Also re-arm the mesh when it has been torn
      // down externally (Rescan button, manual `destroyMesh()` in
      // devtools, or a signalling failure that dropped STATE.armed
      // to false). Previously the sync loop only re-inited on a
      // session identity change, so a Rescan-then-idle would leave
      // the tab stuck on Cloud forever until logout+login. Reading
      // `getMeshStatus().status === "idle"` here is O(1) and only
      // triggers an initMesh call when needed.
      let meshTornDown = false;
      try { meshTornDown = getMeshStatus().status === "idle"; } catch (_) { meshTornDown = false; }
      // Same identity AND mesh still armed? Nothing to do.
      if (picked.token === currentToken && picked.role === currentRole && !meshTornDown) return;
      currentToken = picked.token;
      currentRole = picked.role;
      try {
        // Session 27.73 — Pass the human-readable device label so
        // peers can render "iPad · Staff", "Windows PC · Owner" etc.
        // in the LAN status popover instead of raw UUIDs.
        let deviceLabel = null;
        try { deviceLabel = getDeviceLabel(); } catch (_) { deviceLabel = null; }
        initMesh({
          baseUrl: BACKEND_URL,
          token: picked.token,
          businessId: String(businessId),
          role: picked.role,
          deviceLabel,
        });
      } catch (_) { /* noop */ }
    };

    // Register the mesh event bridge once — it stays alive for the
    // lifetime of the tab.
    unsubMesh = subscribeMesh((envelope, meta) => {
      // 1) Apply to the local database so any dashboard reading from
      //    IDB stays in step even if the cloud WS is slow.
      try { applyRemoteEvent(envelope); } catch (_) { /* noop */ }
      // 1.5) Session 30.26 — Feed the SAME event into the realtime bus
      //    (shaped like a cloud message) so Kitchen / Bar / Runner /
      //    Staff handlers react instantly when the internet is down and
      //    only the router is up. Dedupes repeated copies by change_id.
      try {
        import("@/lib/meshBridge").then((m) => m.bridgeMeshEnvelope(envelope, meta)).catch(() => {});
      } catch (_) { /* noop */ }
      // 2) Fire a DOM event any dashboard can listen to. This is the
      //    hot-path signal that makes LAN delivery feel instant.
      try {
        if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
          window.dispatchEvent(new CustomEvent("nfcs:mesh-event", { detail: { envelope, meta } }));
        }
      } catch (_) { /* noop */ }
      // 3) Also nudge the existing LOCAL_EVENT_APPLIED listeners so
      //    dashboards that already refetch on local writes react to
      //    remote-originated events too.
      try {
        if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
          window.dispatchEvent(new CustomEvent(LOCAL_EVENT_APPLIED, { detail: envelope }));
        }
      } catch (_) { /* noop */ }
    });

    // Session 27.8 — Replace the placeholder cloud-relay coordinator
    // with a LAN-aware shim so every `localWrite()` call automatically
    // attempts direct WebRTC delivery to peers on the same restaurant
    // Wi-Fi before the cloud sync loop kicks in. The cloud loop still
    // runs in parallel (see eventSync.js) — it's the durable audit
    // trail; the mesh is the fast lane.
    try {
      setCoordinator({
        kind: "lan-mesh",
        async broadcast(envelope) {
          try { fireAndForgetMesh(envelope, { targetRole: "*" }); } catch (_) { /* noop */ }
          return { relayed: true, kind: "lan-mesh", change_id: envelope && envelope.change_id };
        },
        subscribe(handler) {
          return subscribeMesh((envelope) => {
            try { handler(envelope); } catch (_) { /* isolate */ }
          });
        },
        isLeader() { return false; },
        async announce() { /* handled by initMesh's peer_joined broadcast */ },
        status() {
          try {
            const snap = getMeshStatus();
            return {
              role: "lan-mesh",
              peer_count: snap.peerCount,
              cloud_sync_enabled: true,
              mesh_status: snap.status,
            };
          } catch (_) {
            return { role: "lan-mesh", peer_count: 0, cloud_sync_enabled: true };
          }
        },
      });
    } catch (_) { /* coordinator install failed — writes still work via cloud */ }

    sync();
    // Poll — cheap and robust vs. cross-tab storage events which
    // don't fire on the tab that made the change.
    const interval = window.setInterval(sync, 1500);
    // Also react to browser online/offline transitions immediately.
    const onOnline = () => sync();
    window.addEventListener("online", onOnline);

    // Session 27.77 — mDNS discovery loop. Every device continuously
    // scans the LAN for other NFCS services. Any peer we find gets
    // captured into the LAN peer cache so a future offline reconnect
    // has the IPv4 to try directly. This runs from the moment the app
    // mounts — the coordinator-side advertisement is tied to
    // `isCoordinator` in the CoordinatorElectionGate. Silent no-op
    // when the plugin isn't available (PWA / preview / Tauri v1).
    //
    // Session 27.78 — ALSO wires up the native LAN signalling relay
    // (Tauri Windows only right now — Android joins in a follow-up).
    // On peer devices we poll the inbox every 2s while cloud is down.
    let mdnsStop = () => {};
    let signalPollTimer = null;
    (async () => {
      try {
        const mod = await import("@/lib/mdnsDiscovery");
        const cache = await import("@/lib/lanPeerCache");
        mdnsStop = await mod.startDiscovery(
          (peer) => {
            try {
              cache.recordLanPeer({
                device_id: peer.device_id,
                device_label: peer.device_label,
                role: peer.role,
                ipv4: peer.ipv4,
                port: peer.port,
                source: "mdns",
              });
            } catch (_) { /* noop */ }
          },
          () => { /* keep cache — expiry is time-based, not event-based */ },
        );
        // Also engage the Tauri-side signalling stack if available.
        const sigMod = await import("@/lib/lanSignalling");
        const selfDeviceId = (() => {
          try { return getDeviceId(); } catch (_) { return null; }
        })();
        if (selfDeviceId) {
          const signaller = sigMod.getSharedSignaller(selfDeviceId);
          const info = await signaller.detect();
          if (info.tauri || info.capacitor) {
            await signaller.startBrowsing();
            // Merge Tauri-discovered peers into the JS cache so both
            // discovery paths converge on one list.
            const nativeStore = await import("@/lib/lanPeerCache");
            signalPollTimer = setInterval(async () => {
              try {
                const peers = await signaller.discoveredPeers();
                for (const p of peers) {
                  if (!p || !p.device_id) continue;
                  nativeStore.recordLanPeer({
                    device_id: p.device_id,
                    device_label: p.device_label,
                    role: p.role,
                    ipv4: p.ipv4,
                    port: p.port,
                    source: "mdns",
                  });
                }
              } catch (_) { /* noop */ }
            }, 3000);
          }
        }
      } catch (_) { /* noop */ }
    })();

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("online", onOnline);
      try { unsubMesh(); } catch (_) { /* noop */ }
      try { destroyMesh(); } catch (_) { /* noop */ }
      try { mdnsStop(); } catch (_) { /* noop */ }
      if (signalPollTimer) try { clearInterval(signalPollTimer); } catch (_) { /* noop */ }
    };
  }, []);

  // Capacitor deep-link handler.
  //
  // When the user taps an install link (e.g. from an admin-generated
  // QR code) on a device that already has the APK installed, Android
  // fires an intent that Capacitor routes to us via `appUrlOpen` /
  // `getLaunchUrl`. Both entry points converge here.
  //
  // The install token is stashed in sessionStorage and the router
  // sends the user straight to /install/setup, where the existing
  // pre-auth snapshot download flow picks it up automatically —
  // exactly the same code path that the browser version uses.
  //
  // No-ops on browser / desktop — the `getPlatform().isNative` check
  // inside onInstallLinkIntent handles that.
  useEffect(() => {
    let unsubscribe = () => {};
    (async () => {
      try {
        unsubscribe = await onInstallLinkIntent(({ token, source, url }) => {
          try {
            window.sessionStorage.setItem("nfcs.pendingInstallToken", token);
            window.sessionStorage.setItem("nfcs.pendingInstallSource", source || "");
            window.sessionStorage.setItem("nfcs.pendingInstallUrl", url || "");
          } catch (_) { /* noop */ }
          // Only route to /install/setup if we're not already deep in
          // an installed session — a passing tap on the link while a
          // user is mid-order shouldn't yank them out.
          try {
            const path = window.location.pathname || "";
            if (path === "/" || path.startsWith("/install") || path.startsWith("/menu")) {
              navigate("/install/setup", { replace: true });
            }
          } catch (_) { /* noop */ }
        });
      } catch (_) { /* noop */ }
    })();
    return () => { try { unsubscribe(); } catch (_) { /* noop */ } };
  }, [navigate]);

  return null;
}


/**
 * FloatingSettingsButton — small gear icon that appears on every route
 * except the customer-facing menu / order screens. Gives operators a
 * one-tap escape hatch to the SettingsPage from any dashboard without
 * having to inject the button into each dashboard's individual header.
 */
export function FloatingSettingsButton() {
  const location = useLocation();
  const navigate = useNavigate();
  // The Settings page is entirely PWA-maintenance surface (device
  // pairing, thermal-printer pairing, offline queue inspector, cache
  // controls). It is meaningless in a regular browser tab — so the
  // floating gear should ONLY appear when the app is running as an
  // installed PWA (`display-mode: standalone`). Reported in Session
  // 27.52 by the owner viewing the dashboard on Edge on desktop: the
  // gear icon was hovering over the dashboard even though there was
  // no PWA context to manage.
  //
  // We subscribe to the display-mode media query so if the user
  // installs the app mid-session (or, less commonly, un-installs and
  // reloads) the button shows / hides without a full app reload.
  const [isStandalone, setIsStandalone] = useState(() => {
    try { return isStandaloneLaunch(); } catch (_) { return false; }
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(display-mode: standalone)");
    const handler = () => {
      try { setIsStandalone(isStandaloneLaunch()); } catch (_) { /* noop */ }
    };
    // Older Safari uses addListener / removeListener.
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);
  if (!isStandalone) return null;
  // Session 30.11 — the installed apps no longer expose the testing-era
  // Settings gear to normal users. (The /settings route itself stays
  // available for internal/admin use.)
  try { if (isInstalledAppEnvironment()) return null; } catch (_) { /* noop */ }
  // Hide on customer-facing / setup routes where a persistent gear
  // would only add noise.
  const hiddenOn = ["/", "/menu", "/install", "/install/setup", "/settings"];
  const isCustomerOrder = location.pathname.startsWith("/order/") || location.pathname.startsWith("/t/");
  // Waiters explicitly asked for the floating gear to be removed from
  // the Staff dashboard — the Actions dropdown already covers logout /
  // tips / handover, and the gear was cluttering the working area.
  // PWA maintenance stays reachable from the Owner / Kitchen / Bar /
  // Admin dashboards.
  const isStaff = location.pathname.startsWith("/staff");
  if (hiddenOn.includes(location.pathname) || isCustomerOrder || isStaff) return null;
  // Only surface for authenticated dashboards — check for ANY active
  // role session in sessionStorage. Login screens don't get the gear.
  let hasAnySession = false;
  forEachStoredSession((_role, s) => { if (s && s.token) { hasAnySession = true; return true; } return false; }, { scope: "tab" });
  if (!hasAnySession) return null;
  return (
    <button
      type="button"
      onClick={() => navigate("/settings")}
      className="fixed bottom-4 right-4 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card/90 text-foreground shadow-lg backdrop-blur transition-transform hover:scale-105 hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]"
      aria-label="Open settings"
      data-testid="floating-settings-button"
    >
      <Settings className="h-5 w-5" />
    </button>
  );
}


/**
 * Sets a `data-season` attribute on <html> so seasonal CSS variables
 * defined in index.css apply across the entire app.
 * Re-evaluates on mount and once per hour to handle long-running sessions
 * crossing a season boundary.
 */
export function SeasonalThemeApplier() {
  useEffect(() => {
    const apply = () => {
      const season = getSouthAfricanSeason(new Date());
      document.documentElement.setAttribute("data-season", season);
    };
    apply();
    const interval = setInterval(apply, 60 * 60 * 1000); // hourly
    return () => clearInterval(interval);
  }, []);
  return null;
}
